import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';
import ContactMessageCard from '@/features/contact-messages/components/ContactMessageCard';
import {
  useContactMessagesPage,
  type StatusFilter,
} from '@/features/contact-messages/hooks/useContactMessagesPage.hook';

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'New', value: 'new' },
  { label: 'Read', value: 'read' },
  { label: 'Archived', value: 'archived' },
];

const ContactMessagesPage: FC = () => {
  const vm = useContactMessagesPage();

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <header>
        <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
          Contact Messages
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
          {vm.total.toLocaleString()} message{vm.total === 1 ? '' : 's'} from the app Contact screen
        </p>
      </header>

      {/* Status filter chips */}
      <div className="flex gap-2">
        {STATUS_FILTERS.map(({ label, value }) => (
          <button
            key={value}
            type="button"
            onClick={() => vm.onChangeStatus(value)}
            className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
              vm.statusFilter === value
                ? 'border-brand-500 bg-brand-500 text-white'
                : 'border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:border-brand-300 hover:text-brand-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* List / states */}
      {vm.isLoading ? (
        <div className="flex min-h-64 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
          Loading messages…
        </div>
      ) : vm.isError ? (
        <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-sm font-semibold text-[var(--color-danger-ink)]">
            Couldn't load messages
          </p>
          <p className="text-xs text-[var(--color-ink-muted)]">
            {vm.errorMessage ?? 'Please try again.'}
          </p>
        </div>
      ) : vm.messages.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
          <span className="text-3xl">💬</span>
          <p className="text-sm font-semibold text-[var(--color-ink)]">No messages</p>
          <p className="text-xs text-[var(--color-ink-muted)]">
            Messages sent from the app Contact screen show up here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {vm.messages.map((message) => (
            <ContactMessageCard
              key={message.id}
              message={message}
              isMutating={vm.mutatingId === message.id}
              onMarkRead={vm.onMarkRead}
              onArchive={vm.onArchive}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {vm.totalPages > 1 ? (
        <div className="flex justify-end">
          <Pagination page={vm.page} totalPages={vm.totalPages} onChange={vm.onChangePage} />
        </div>
      ) : null}
    </div>
  );
};

export default ContactMessagesPage;
